import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import {
  setGSTRateThunk,
  setCustomChargeThunk,
  getChargesConfigThunk,
  getAllRatesWithChargesThunk,
} from "../../../store/thunks/rateThunks";
import ChargesConfiguration from "./ChargesConfiguration";
import { useCurrentBranch } from "../../../hooks/useCurrentBranch";
import { Loader } from "lucide-react";

const ChargesManager = () => {
  const dispatch = useDispatch();
  const { currentBranch, hasBranchAccess } = useCurrentBranch();

  const { charges, loading, saving } = useSelector((state) => state.rate);

  const [activeTab, setActiveTab] = useState("gst");
  const [selectedMetalType, setSelectedMetalType] = useState("GOLD");

  // Fetch existing charges configuration on mount
  useEffect(() => {
    if (hasBranchAccess && currentBranch) {
      fetchChargesConfig();
    }
  }, [currentBranch, selectedMetalType]);

  const fetchChargesConfig = async () => {
    try {
      // Fetch all rates with charges to get comprehensive data
      await dispatch(
        getAllRatesWithChargesThunk({
          branchCode: currentBranch,
          metalType: selectedMetalType,
        })
      ).unwrap();

      // Also fetch specific charges config
      await dispatch(
        getChargesConfigThunk({
          branchCode: currentBranch,
          metalType: selectedMetalType,
        })
      ).unwrap();
    } catch (error) {
      console.error("Failed to fetch charges:", error);
    }
  };

  const handleGSTSave = async (gstData) => {
    try {
      await dispatch(
        setGSTRateThunk({
          ...gstData,
          branchCode: currentBranch,
        })
      ).unwrap();

      toast.success("GST configuration saved successfully");
      fetchChargesConfig(); // Refresh data
    } catch (error) {
      toast.error(error.message || "Failed to save GST configuration");
    }
  };

  const handleCustomChargeSave = async (chargeData) => {
    try {
      console.log("Saving custom charge:", chargeData);
      await dispatch(
        setCustomChargeThunk({
          ...chargeData,
          branchCode: currentBranch,
        })
      ).unwrap();

      console.log("Custom charge save successful");
      toast.success(
        `${chargeData.chargeType} configuration saved successfully`
      );
      fetchChargesConfig(); // Refresh data
    } catch (error) {
      console.error("Custom charge save failed:", error);
      toast.error(
        error.message || `Failed to save ${chargeData.chargeType} configuration`
      );
    }
  };

  const tabs = [
    { id: "gst", label: "GST", description: "Goods & Service Tax" },
    { id: "making", label: "Making", description: "Making charges" },
    { id: "wastage", label: "Wastage", description: "Material wastage" },
    { id: "hallmark", label: "Hallmark", description: "BIS hallmark charges" },
  ];

  // if (loading.charges) {
  //   return (
  //     <div className="flex items-center justify-center h-64">
  //       <Loader className="w-8 h-8 animate-spin text-blue-600" />
  //     </div>
  //   );
  // }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold mb-2">Charges Configuration</h1>
            <p className="text-gray-600">
              Manage GST, making charges, wastage, and other fees for your
              products
            </p>
          </div>
          <div className="text-right">
            {currentBranch && (
              <div className="text-sm text-gray-600 mb-2">
                Branch: <span className="font-semibold">{currentBranch}</span>
              </div>
            )}
            <select
              value={selectedMetalType}
              onChange={(e) => setSelectedMetalType(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
            >
              <option value="GOLD">Gold</option>
              <option value="SILVER">Silver</option>
              <option value="PLATINUM">Platinum</option>
            </select>
          </div>
        </div>
      </div>

      {!hasBranchAccess ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">
            Please select a branch to manage charges configuration
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow">
          {/* Tab Navigation */}
          <div className="border-b">
            <nav className="flex">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-4 font-medium border-b-2 transition-all flex-1 ${
                    activeTab === tab.id
                      ? "border-blue-600 text-blue-600 bg-blue-50"
                      : "border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  <div>
                    <div className="font-medium">{tab.label}</div>
                    <div className="text-xs mt-1 font-normal">
                      {tab.description}
                    </div>
                  </div>
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="p-6">
            <ChargesConfiguration
              activeTab={activeTab}
              onGSTSave={handleGSTSave}
              onCustomChargeSave={handleCustomChargeSave}
              branchCode={currentBranch}
              initialData={charges || {}}
              loading={saving.charges}
              selectedMetalTypes={[selectedMetalType]}
            />
          </div>

          {/* Summary Card */}
          {charges && (
            <div className="border-t p-6 bg-gray-50">
              <h3 className="font-semibold mb-3">
                Current Configuration Summary
              </h3>
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">GST:</span>
                  <span className="ml-2 font-medium">
                    {charges.gst?.rate || 3}%
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Making:</span>
                  <span className="ml-2 font-medium">
                    {charges.making?.enabled
                      ? `${charges.making.value}${
                          charges.making.type === "PERCENTAGE" ? "%" : "₹"
                        }`
                      : "Disabled"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Wastage:</span>
                  <span className="ml-2 font-medium">
                    {charges.wastage?.enabled
                      ? `${charges.wastage.value}${
                          charges.wastage.type === "PERCENTAGE" ? "%" : "₹"
                        }`
                      : "Disabled"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Hallmark:</span>
                  <span className="ml-2 font-medium">
                    {charges.hallmark?.enabled
                      ? `₹${charges.hallmark.charge}`
                      : "Disabled"}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChargesManager;
